import { Component, Element, JSX, Prop, Watch, h } from '@stencil/core'

import { ButtonIconPlacement, ButtonType, ButtonVariant } from './air-button.types'

import { isUndefined } from '../../utils/helpers'

enum ButtonProgressRange {
  Min = 0,
  Max = 100,
}

@Component({
  tag: 'air-button',
  styleUrl: 'air-button.scss',
  shadow: true,
})
export class AirButton {
  /** Reference to the host element */
  @Element() host!: HTMLAirButtonElement

  /** Specifies type and event button dispatches */
  @Prop({ mutable: true }) type: ButtonType = 'button'

  /** Handles type change and fallback */
  @Watch('type')
  watchType(newValue: ButtonType): void {
    this.type = this.handleTypeFallback(newValue)
  }

  /** Specifies button style */
  @Prop({ mutable: true }) variant: ButtonVariant = 'primary'

  /** Handles variant change and fallback */
  @Watch('variant')
  watchVariant(newValue: ButtonVariant): void {
    this.variant = this.handleVariantFallback(newValue)
  }

  /** Specifies if button should be disabled */
  @Prop() disabled = false

  /** Percentage of button loading */
  @Prop() progress: number = ButtonProgressRange.Min

  /** Specifies what icon from asset library is shown */
  @Prop() icon?: string

  /** Specifies on which side icon is shown */
  @Prop({ mutable: true }) iconPlacement: ButtonIconPlacement = 'left'

  /** Handles icon placement change and fallback */
  @Watch('iconPlacement')
  watchPlacement(newValue: ButtonIconPlacement): void {
    this.iconPlacement = this.handleIconPlacementFallback(newValue)
  }

  componentWillLoad(): void {
    this.type = this.handleTypeFallback(this.type)
    this.variant = this.handleVariantFallback(this.variant)
    this.iconPlacement = this.handleIconPlacementFallback(this.iconPlacement)
  }

  private handleTypeFallback(value: ButtonType): ButtonType {
    const list: ButtonType[] = ['button', 'submit', 'reset']
    return list.includes(value) ? value : list[0]
  }

  private handleVariantFallback(value: ButtonVariant): ButtonVariant {
    const list: ButtonVariant[] = ['primary', 'secondary', 'tertiary']
    return list.includes(value) ? value : list[0]
  }

  private handleIconPlacementFallback(value: ButtonIconPlacement): ButtonIconPlacement {
    const list: ButtonIconPlacement[] = ['left', 'right']
    return list.includes(value) ? value : list[0]
  }

  private hasProgress(): boolean {
    return this.progress > ButtonProgressRange.Min
  }

  private onClickHandler = (): void => {
    const closestForm = this.host.closest('form')
    const eventType = closestForm?.[this.type]

    if (typeof eventType === 'function') {
      closestForm?.[this.type]()
    }
  }

  private renderProgressLine(): JSX.Element {
    const { progress } = this
    return this.hasProgress() ? (
      <div class="button-progress-line-wrapper">
        <div
          class="button-progress-line"
          style={{
            width: `${progress > ButtonProgressRange.Max ? ButtonProgressRange.Max : progress}%`,
          }}
        ></div>
      </div>
    ) : null
  }

  private renderIcon(): JSX.Element | null {
    if (isUndefined(this.icon) || this.icon === '') return null
    return <air-icon class="button-icon" name={this.icon}></air-icon>
  }

  render(): JSX.Element {
    const { type, variant, disabled, iconPlacement } = this
    const isIconDefined = !isUndefined(this.icon)
    const buttonClasses = {
      [`button`]: true,
      [`button-${variant}`]: true,
      [`button-loading`]: this.hasProgress(),
      [`button-with-icon`]: isIconDefined,
      [`button-icon-placement-${iconPlacement}`]: isIconDefined,
    }
    return (
      <div class="button-wrapper">
        {this.renderProgressLine()}
        <button
          type={type}
          class={buttonClasses}
          disabled={disabled || this.hasProgress()}
          onClick={this.onClickHandler}
        >
          {iconPlacement === 'left' ? this.renderIcon() : null}
          <slot />
          {iconPlacement === 'right' ? this.renderIcon() : null}
        </button>
      </div>
    )
  }
}
